/* eslint-disable no-undefined */
/* eslint-disable max-lines -- flat registry of every plugin; grows with each addition */
/**
 * Plugin Manifest - Single source of truth for all plugin registrations.
 *
 * Array order determines initialization order.
 *
 * Adding a new plugin:
 * 1. Add a PluginDescriptor entry here (import path, class name, default config)
 * 2. Optionally add the config key to KeepTrackPluginsConfiguration for type-safe access
 *
 * Pro imports are guarded by __IS_PRO__ (compile-time constant from DefinePlugin).
 * In OSS builds, __IS_PRO__ is false → the ternary evaluates to undefined →
 * rspack never resolves the plugins-pro path → no stub files needed.
 */
import type { PluginDescriptor } from './plugin-descriptor';
import { externalPluginManifest } from './plugin-manifest.external.generated';
import { satInfoBoxOrbitalConfigurationDefaults } from './sat-info-box-orbital/sat-info-box-orbital-settings';

export const pluginManifest: PluginDescriptor[] = [
  // ── Always-enabled infrastructure ──────────────────────────────────────────
  {
    configKey: 'Telemetry',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/telemetry/telemetry') : undefined,
    proClassName: 'Telemetry',
    defaultConfig: { enabled: true },
    alwaysEnabled: true,
  },
  {
    configKey: 'SelectSatManager',
    ossImport: () => import('./select-sat-manager/select-sat-manager'),
    ossClassName: 'SelectSatManager',
    defaultConfig: { enabled: true },
    alwaysEnabled: true,
  },

  // ── Data plugins (must load before catalog) ──────────────────────────────
  {
    configKey: 'StarsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/stars/stars-plugin') : undefined,
    proClassName: 'StarsPlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'VmagDatabasePlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/vmag-database/vmag-database-plugin') : undefined,
    proClassName: 'VmagDatabasePlugin',
    defaultConfig: { enabled: true },
  },

  // ── Core UI ────────────────────────────────────────────────────────────────
  {
    configKey: 'ScenarioManagementPlugin',
    ossImport: () => import('./scenario-management/scenario-management'),
    ossClassName: 'ScenarioManagementPlugin',
    defaultConfig: { enabled: true, order: 1 },
  },
  {
    configKey: 'ScenarioManagementMenu',
    ossImport: () => import('./scenario-management/scenario-management-menu'),
    ossClassName: 'ScenarioManagementMenu',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/scenario-management-pro/scenario-management-pro') : undefined,
    proClassName: 'ScenarioManagementMenuPro',
    defaultConfig: { enabled: true, order: 2 },
  },
  {
    configKey: 'TopMenu',
    ossImport: () => import('./top-menu/top-menu'),
    ossClassName: 'TopMenu',
    defaultConfig: { enabled: true },
  },
  {
    // Replaced by AccessTimelinePlugin in Pro builds; still the default in OSS.
    configKey: 'TimeSlider',
    ossImport: () => import('./time-slider/time-slider'),
    ossClassName: 'TimeSlider',
    defaultConfig: { enabled: !__IS_PRO__, order: 1001 },
  },
  {
    configKey: 'AccessTimelinePlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/access-timeline/access-timeline') : undefined,
    proClassName: 'AccessTimelinePlugin',
    defaultConfig: { enabled: true, order: 1001 },
  },
  {
    configKey: 'TooltipsPlugin',
    ossImport: () => import('./tooltips/tooltips'),
    ossClassName: 'TooltipsPlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'UserAccountPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/user-account/user-account') : undefined,
    proClassName: 'UserAccountPlugin',
    defaultConfig: { enabled: true, order: 2 },
  },
  {
    configKey: 'DebugMenuPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/debug/debug') : undefined,
    proClassName: 'DebugMenuPlugin',
    defaultConfig: { enabled: true, order: 0 },
  },

  // ── Satellite Info Box ─────────────────────────────────────────────────────
  {
    configKey: 'SatInfoBoxCore',
    ossImport: () => import('./sat-info-box/sat-info-box'),
    ossClassName: 'SatInfoBox',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'SatInfoBoxActions',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/sat-info-box-actions/sat-info-box-actions') : undefined,
    proClassName: 'SatInfoBoxActions',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'SatInfoBoxLinks',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/sat-info-box-links/sat-info-box-links') : undefined,
    proClassName: 'SatInfoBoxLinks',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'SatInfoBoxOrbital',
    ossImport: () => import('./sat-info-box-orbital/sat-info-box-orbital'),
    ossClassName: 'SatInfoBoxOrbital',
    defaultConfig: satInfoBoxOrbitalConfigurationDefaults,
  },
  // Off by design: OrbitGuard partner demo; fetches maneuver history from an external API at a hardcoded IP with a shared bearer token, so it is opt-in only (disabled since 241631cb).
  {
    configKey: 'SatInfoBoxManeuver',
    ossImport: () => import('./sat-info-box-orbit-guard/sat-info-box-orbit-guard'),
    ossClassName: 'SatInfoBoxOrbitGuard',
    defaultConfig: { enabled: false },
  },
  {
    configKey: 'SatInfoBoxObject',
    ossImport: () => import('./sat-info-box-object/sat-info-box-object'),
    ossClassName: 'SatInfoBoxObject',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'SatInfoBoxMission',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/sat-info-box-mission/sat-info-box-mission') : undefined,
    proClassName: 'SatInfoBoxMission',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'SatInfoBoxSponsor',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/sat-info-box-sponsor/sat-info-box-sponsor') : undefined,
    proClassName: 'SatInfoBoxSponsor',
    defaultConfig: { enabled: true },
  },
  // Off by design: monetization opt-in; the leaderboard ad banner is deliberately not shown by default (disabled in 7593131b).
  {
    configKey: 'BottomBannerSponsor',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/bottom-banner-sponsor/bottom-banner-sponsor') : undefined,
    proClassName: 'BottomBannerSponsor',
    defaultConfig: { enabled: false },
  },
  {
    configKey: 'SatInfoBoxSensor',
    ossImport: () => import('./sat-info-box-sensor/sat-info-box-sensor'),
    ossClassName: 'SatInfoBoxSensor',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'SatInfoBoxDoppler',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/sat-info-box-doppler/sat-info-box-doppler') : undefined,
    proClassName: 'SatInfoBoxDoppler',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'DateTimeManager',
    ossImport: () => import('./date-time-manager/date-time-manager'),
    ossClassName: 'DateTimeManager',
    defaultConfig: { enabled: true },
  },

  // ── Top Menu Plugins (loaded right to left) ────────────────────────────────
  {
    configKey: 'GithubLinkPlugin',
    ossImport: () => import('./github-link/github-link'),
    ossClassName: 'GithubLinkPlugin',
    defaultConfig: { enabled: true },
  },
  // Off by design: example TopMenuPlugin social link; plugin doc says it exists as a sample and its icon still needs restyling to match the GitHub icon (disabled since 241631cb).
  {
    configKey: 'LinkedInLinkPlugin',
    ossImport: () => import('./linkedin-link/linkedin-link'),
    ossClassName: 'LinkedInLinkPlugin',
    defaultConfig: { enabled: false },
  },
  {
    configKey: 'ClassificationBar',
    ossImport: () => import('./classification-bar/classification-bar'),
    ossClassName: 'ClassificationBar',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'SoundToggle',
    ossImport: () => import('./sound-toggle/sound-toggle'),
    ossClassName: 'SoundToggle',
    defaultConfig: { enabled: true },
  },

  // ── Scene Plugins ──────────────────────────────────────────────────────────
  {
    configKey: 'EarthAtmosphere',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/earth-atmosphere/earth-atmosphere') : undefined,
    proClassName: 'EarthAtmosphere',
    defaultConfig: { enabled: true },
  },

  // ── Bottom Menu Plugins ────────────────────────────────────────────────────
  {
    configKey: 'SensorListPlugin',
    ossImport: () => import('./sensor-list/sensor-list'),
    ossClassName: 'SensorListPlugin',
    defaultConfig: { enabled: true, order: 10 },
  },
  {
    configKey: 'SensorInfoPlugin',
    ossImport: () => import('./sensor/sensor-info-plugin'),
    ossClassName: 'SensorInfoPlugin',
    defaultConfig: { enabled: true, order: 11 },
  },
  {
    configKey: 'CustomSensorPlugin',
    ossImport: () => import('./sensor/custom-sensor-plugin'),
    ossClassName: 'CustomSensorPlugin',
    defaultConfig: { enabled: true, order: 12 },
  },
  {
    configKey: 'SensorFov',
    ossImport: () => import('./sensor-fov/sensor-fov'),
    ossClassName: 'SensorFov',
    defaultConfig: { enabled: true, order: 13 },
  },
  {
    configKey: 'SensorSurvFence',
    ossImport: () => import('./sensor-surv/sensor-surv-fence'),
    ossClassName: 'SensorSurvFence',
    defaultConfig: { enabled: true, order: 14 },
  },
  {
    configKey: 'ShortTermFences',
    ossImport: () => import('./short-term-fences/short-term-fences'),
    ossClassName: 'ShortTermFences',
    defaultConfig: { enabled: true, order: 15 },
  },
  {
    configKey: 'LookAnglesPlugin',
    ossImport: () => import('./sensor/look-angles-plugin'),
    ossClassName: 'LookAnglesPlugin',
    defaultConfig: { enabled: true, order: 20 },
  },
  // Off because not ready: self-described @experimental and NOT PRODUCTION READY; RF model is first-order and unvalidated, surfaced only in the EXPERIMENTAL menu mode (disabled in cd3af70c).
  {
    configKey: 'LinkBudgetPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/link-budget/link-budget') : undefined,
    proClassName: 'LinkBudgetPlugin',
    defaultConfig: { enabled: false, order: 94 },
    isLoginRequired: true,
  },
  {
    configKey: 'MultiSensorLookAnglesPlugin',
    ossImport: () => import('./sensor/multi-sensor-look-angles-plugin'),
    ossClassName: 'MultiSensorLookAnglesPlugin',
    defaultConfig: { enabled: true, order: 21 },
  },
  {
    // Replaced by AccessTimelinePlugin in Pro builds; still the default in OSS.
    configKey: 'SensorTimeline',
    ossImport: () => import('./timeline-sensor/sensor-timeline'),
    ossClassName: 'SensorTimeline',
    defaultConfig: { enabled: !__IS_PRO__, order: 30 },
  },
  {
    // Replaced by AccessTimelinePlugin in Pro builds; still the default in OSS.
    configKey: 'SatelliteTimeline',
    ossImport: () => import('./timeline-satellite/satellite-timeline'),
    ossClassName: 'SatelliteTimeline',
    defaultConfig: { enabled: !__IS_PRO__, order: 31 },
  },
  {
    configKey: 'WatchlistPlugin',
    ossImport: () => import('./watchlist/watchlist'),
    ossClassName: 'WatchlistPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/watchlist/watchlist') : undefined,
    proClassName: 'WatchlistProPlugin',
    defaultConfig: { enabled: true, order: 40 },
    isLoginRequired: true,
  },
  {
    configKey: 'WatchlistOverlay',
    ossImport: () => import('./watchlist/watchlist-overlay'),
    ossClassName: 'WatchlistOverlay',
    defaultConfig: { enabled: true, order: 41 },
  },
  {
    configKey: 'WatchlistFilterPlugin',
    ossImport: () => import('./watchlist-filter/watchlist-filter'),
    ossClassName: 'WatchlistFilterPlugin',
    defaultConfig: { enabled: true, order: 42 },
  },
  {
    configKey: 'ReportsPlugin',
    ossImport: () => import('./reports/reports'),
    ossClassName: 'ReportsPlugin',
    defaultConfig: { enabled: true, order: 50 },
  },
  {
    configKey: 'PolarPlotPlugin',
    ossImport: () => import('./polar-plot/polar-plot'),
    ossClassName: 'PolarPlotPlugin',
    defaultConfig: { enabled: true, order: 60 },
  },
  {
    configKey: 'TheSpaceDevLaunchCalendarPlugin',
    ossImport: () => import('./thespacedev-launch-calendar/thespacedev-launch-calendar'),
    ossClassName: 'TheSpaceDevLaunchCalendarPlugin',
    defaultConfig: { enabled: true, order: 350 },
  },
  {
    configKey: 'SeismicActivityPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/seismic-activity/seismic-activity') : undefined,
    proClassName: 'SeismicActivityPlugin',
    defaultConfig: { enabled: true, order: 355 },
  },
  {
    configKey: 'AuroraPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/aurora/aurora') : undefined,
    proClassName: 'AuroraPlugin',
    defaultConfig: { enabled: true, order: 356 },
  },
  {
    configKey: 'NaturalEventsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/natural-events/natural-events') : undefined,
    proClassName: 'NaturalEventsPlugin',
    defaultConfig: { enabled: true, order: 357 },
  },
  {
    configKey: 'FindSatPlugin',
    ossImport: () => import('./find-sat/find-sat'),
    ossClassName: 'FindSatPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/find-sat/find-sat') : undefined,
    proClassName: 'FindSatPro',
    defaultConfig: { enabled: true, order: 80 },
    isLoginRequired: true,
  },
  {
    configKey: 'ProximityOps',
    ossImport: () => import('./proximity-ops/proximity-ops'),
    ossClassName: 'ProximityOps',
    defaultConfig: { enabled: true, order: 81 },
  },
  {
    configKey: 'OrbitReferences',
    ossImport: () => import('./orbit-references/orbit-references'),
    ossClassName: 'OrbitReferences',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'Collisions',
    ossImport: () => import('./collisions/collisions'),
    ossClassName: 'Collisions',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/collisions-pro/collisions-pro') : undefined,
    proClassName: 'CollisionsPro',
    defaultConfig: { enabled: true, order: 90 },
  },
  {
    configKey: 'TocaPocaPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/toca-poca-plugin/toca-poca-plugin') : undefined,
    proClassName: 'TocaPocaPlugin',
    defaultConfig: { enabled: true, order: 82 },
    isLoginRequired: true,
  },
  {
    configKey: 'NeighborhoodWatch',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/neighborhood-watch/neighborhood-watch') : undefined,
    proClassName: 'NeighborhoodWatch',
    defaultConfig: { enabled: true, order: 83 },
    isLoginRequired: true,
  },
  // Off by design: OrbitGuard partner demo; queries the external OrbitGuard maneuver API at a hardcoded IP with a shared bearer token, so it is opt-in only (disabled since 241631cb).
  {
    configKey: 'OrbitGuardMenuPlugin',
    ossImport: () => import('./orbit-guard-menu/orbit-guard-menu'),
    ossClassName: 'OrbitGuardMenuPlugin',
    defaultConfig: { enabled: false, order: 91 },
  },
  {
    configKey: 'Reentries',
    ossImport: () => import('./reentries/reentries'),
    ossClassName: 'Reentries',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/reentries/reentries-pro') : undefined,
    proClassName: 'ReentriesPro',
    defaultConfig: { enabled: true, order: 92 },
  },
  {
    configKey: 'Breakup',
    ossImport: () => import('./breakup/breakup'),
    ossClassName: 'Breakup',
    defaultConfig: { enabled: true, order: 73 },
  },
  {
    configKey: 'BreakupAnalysis',
    ossImport: () => import('./breakup-analysis/breakup-analysis'),
    ossClassName: 'BreakupAnalysis',
    defaultConfig: { enabled: true, order: 74 },
  },
  {
    configKey: 'DebrisScreening',
    ossImport: () => import('./debris-screening/debris-screening'),
    ossClassName: 'DebrisScreening',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/debris-screening-pro/debris-screening-pro') : undefined,
    proClassName: 'DebrisScreeningPro',
    defaultConfig: { enabled: true, order: 280 },
  },
  {
    configKey: 'transponderChannelData',
    ossImport: () => import('./transponder-channel-data/transponder-channel-data'),
    ossClassName: 'TransponderChannelData',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'StkFileHandler',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/stk-file-handler/stk-file-handler') : undefined,
    proClassName: 'StkFileHandler',
    defaultConfig: { enabled: true },
    isLoginRequired: true,
  },
  {
    configKey: 'CreateSat',
    ossImport: () => import('./create-sat/create-sat'),
    ossClassName: 'CreateSat',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/create-sat/create-sat') : undefined,
    proClassName: 'CreateSatPro',
    defaultConfig: { enabled: true, order: 70 },
    isLoginRequired: true,
  },
  {
    configKey: 'OemReaderPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/oem-reader/oem-reader') : undefined,
    proClassName: 'OemReaderPlugin',
    defaultConfig: { enabled: true, order: 71.5 },
    isLoginRequired: true,
  },
  {
    configKey: 'ObservationReaderPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/observation-reader/observation-reader') : undefined,
    proClassName: 'ObservationReaderPlugin',
    defaultConfig: { enabled: true, order: 71.7 },
    isLoginRequired: true,
  },
  {
    configKey: 'NeighborhoodHistoryPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/neighborhood-history/neighborhood-history') : undefined,
    proClassName: 'NeighborhoodHistoryPlugin',
    defaultConfig: { enabled: true, order: 71.6 },
  },
  {
    configKey: 'EditSat',
    ossImport: () => import('./edit-sat/edit-sat'),
    ossClassName: 'EditSat',
    defaultConfig: { enabled: true, order: 71 },
  },
  {
    configKey: 'NewLaunch',
    ossImport: () => import('./new-launch/new-launch'),
    ossClassName: 'NewLaunch',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/new-launch/new-launch') : undefined,
    proClassName: 'NewLaunchPro',
    defaultConfig: { enabled: true, order: 72 },
    isLoginRequired: true,
  },
  {
    configKey: 'MissileSimulatorPlugin',
    ossImport: () => import('./missile/missile-simulator-plugin'),
    ossClassName: 'MissileSimulatorPlugin',
    defaultConfig: { enabled: true, order: 74 },
  },
  {
    configKey: 'EarthCenteredView',
    ossImport: () => import('./earth-centered-view/earth-centered-view'),
    ossClassName: 'EarthCenteredView',
    defaultConfig: { enabled: true, order: 149 },
  },
  {
    configKey: 'FlatMapView',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/flat-map-view/flat-map-view') : undefined,
    proClassName: 'FlatMapView',
    defaultConfig: { enabled: true, order: 150 },
    isLoginRequired: true,
  },
  {
    configKey: 'SatelliteFixedView',
    ossImport: () => import('./satellite-fixed-view/satellite-fixed-view'),
    ossClassName: 'SatelliteFixedView',
    defaultConfig: { enabled: true, order: 152 },
  },
  {
    configKey: 'SatelliteEciView',
    ossImport: () => import('./satellite-eci-view/satellite-eci-view'),
    ossClassName: 'SatelliteEciView',
    defaultConfig: { enabled: true, order: 151 },
  },
  {
    configKey: 'SatelliteViewPlugin',
    ossImport: () => import('./satellite-view/satellite-view'),
    ossClassName: 'SatelliteViewPlugin',
    defaultConfig: { enabled: true, order: 152 },
  },
  {
    configKey: 'Planetarium',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/planetarium/planetarium') : undefined,
    proClassName: 'Planetarium',
    defaultConfig: { enabled: true, order: 153 },
  },
  {
    configKey: 'Astronomy',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/astronomy/astronomy') : undefined,
    proClassName: 'Astronomy',
    defaultConfig: { enabled: true, order: 154 },
  },
  {
    configKey: 'FpsView',
    ossImport: () => import('./fps-view/fps-view'),
    ossClassName: 'FpsView',
    defaultConfig: { enabled: true, order: 155 },
  },
  {
    configKey: 'PolarView',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/polar-view/polar-view') : undefined,
    proClassName: 'PolarView',
    defaultConfig: { enabled: true, order: 156 },
    isLoginRequired: true,
  },
  {
    configKey: 'MultiView',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/multi-view/multi-view') : undefined,
    proClassName: 'MultiView',
    defaultConfig: { enabled: true, order: 157 },
    isLoginRequired: true,
  },
  {
    configKey: 'SatelliteFov',
    ossImport: () => import('./satellite-fov/satellite-fov'),
    ossClassName: 'SatelliteFov',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/satellite-fov/satellite-fov') : undefined,
    proClassName: 'SatelliteFovPro',
    defaultConfig: { enabled: true, order: 75 },
    isLoginRequired: true,
  },
  {
    configKey: 'StereoMap',
    ossImport: () => import('./stereo-map/stereo-map'),
    ossClassName: 'StereoMap',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/stereo-map/stereo-map') : undefined,
    proClassName: 'StereoMapPro',
    defaultConfig: { enabled: true, order: 150 },
  },
  {
    configKey: 'NightToggle',
    ossImport: () => import('./night-toggle/night-toggle'),
    ossClassName: 'NightToggle',
    defaultConfig: { enabled: true, order: 310 },
  },
  {
    configKey: 'GraticuleToggle',
    ossImport: () => import('./graticule-toggle/graticule-toggle'),
    ossClassName: 'GraticuleToggle',
    defaultConfig: { enabled: true, order: 311 },
  },
  {
    configKey: 'TerminatorToggle',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/terminator-toggle/terminator-toggle') : undefined,
    proClassName: 'TerminatorToggle',
    defaultConfig: { enabled: true, order: 312 },
  },
  {
    configKey: 'PoliticalMapToggle',
    ossImport: () => import('./political-map-toggle/political-map-toggle'),
    ossClassName: 'PoliticalMapToggle',
    defaultConfig: { enabled: true, order: 312 },
  },
  {
    configKey: 'CloudsToggle',
    ossImport: () => import('./clouds-toggle/clouds-toggle'),
    ossClassName: 'CloudsToggle',
    defaultConfig: { enabled: true, order: 313 },
  },
  {
    configKey: 'HideOtherSatellitesPlugin',
    ossImport: () => import('./hide-other-sats/hide-other-sats'),
    ossClassName: 'HideOtherSatellitesPlugin',
    defaultConfig: { enabled: true, order: 314 },
  },
  {
    configKey: 'SkipInterpolationToggle',
    ossImport: () => import('./skip-interpolation-toggle/skip-interpolation-toggle'),
    ossClassName: 'SkipInterpolationToggle',
    defaultConfig: { enabled: true, order: 315 },
  },
  {
    configKey: 'FovFadePlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/fov-fade/fov-fade') : undefined,
    proClassName: 'FovFadePlugin',
    ossImport: () => import('./fov-fade/fov-fade'),
    ossClassName: 'FovFadePlugin',
    defaultConfig: { enabled: true, order: 316 },
  },
  {
    configKey: 'KeyboardShortcutsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/keyboard-shortcuts/keyboard-shortcuts') : undefined,
    proClassName: 'KeyboardShortcutsPlugin',
    defaultConfig: { enabled: true, order: 314 },
  },
  {
    configKey: 'CovariancePlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/covariance/covariance') : undefined,
    proClassName: 'CovariancePlugin',
    defaultConfig: { enabled: true, order: 316 },
  },
  {
    configKey: 'CovarianceStatsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/covariance/covariance-stats') : undefined,
    proClassName: 'CovarianceStatsPlugin',
    defaultConfig: { enabled: true, order: 317 },
  },
  {
    configKey: 'DopsPlugin',
    ossImport: () => import('./dops/dops'),
    ossClassName: 'DopsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/dops/dops') : undefined,
    proClassName: 'DopsPluginPro',
    defaultConfig: { enabled: true, order: 500 },
    isLoginRequired: true,
  },
  {
    configKey: 'SatConstellations',
    ossImport: () => import('./sat-constellations/sat-constellations'),
    ossClassName: 'SatConstellations',
    defaultConfig: { enabled: true, order: 230 },
  },
  {
    configKey: 'CountriesMenu',
    ossImport: () => import('./countries/countries'),
    ossClassName: 'CountriesMenu',
    defaultConfig: { enabled: true, order: 231 },
  },
  {
    configKey: 'ColorMenu',
    ossImport: () => import('./colors-menu/colors-menu'),
    ossClassName: 'ColorMenu',
    defaultConfig: { enabled: true, order: 232 },
  },
  {
    configKey: 'PlanetsMenuPlugin',
    ossImport: () => import('./planets-menu/planets-menu'),
    ossClassName: 'PlanetsMenuPlugin',
    defaultConfig: { enabled: true, order: 233 },
  },
  {
    configKey: 'DeepSpaceMissionsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/deep-space-missions-menu/deep-space-missions-menu') : undefined,
    proClassName: 'DeepSpaceMissionsPlugin',
    defaultConfig: { enabled: true, order: 234 },
  },
  {
    configKey: 'Screenshot',
    ossImport: () => import('./screenshot/screenshot'),
    ossClassName: 'Screenshot',
    defaultConfig: { enabled: true, order: 450 },
  },
  // Off by design: superseded by TheSpaceDevLaunchCalendarPlugin as the default launch calendar; 54cbbbc6 states "The Gunter calendar now defaults to disabled".
  {
    configKey: 'GunterLaunchCalendar',
    ossImport: () => import('./gunter-launch-calendar/gunter-launch-calendar'),
    ossClassName: 'GunterLaunchCalendar',
    defaultConfig: { enabled: false, order: 351 },
  },
  {
    configKey: 'TimeMachine',
    ossImport: () => import('./time-machine/time-machine'),
    ossClassName: 'TimeMachine',
    defaultConfig: { enabled: true, order: 250 },
  },
  {
    configKey: 'SatellitePhotos',
    ossImport: () => import('./satellite-photos/satellite-photos'),
    ossClassName: 'SatellitePhotos',
    defaultConfig: { enabled: true, order: 240 },
  },
  {
    configKey: 'ScreenRecorder',
    ossImport: () => import('./screen-recorder/screen-recorder'),
    ossClassName: 'ScreenRecorder',
    defaultConfig: { enabled: true, order: 451 },
  },
  {
    configKey: 'ShareMenuPlugin',
    ossImport: () => import('./share-menu/share-menu'),
    ossClassName: 'ShareMenuPlugin',
    defaultConfig: { enabled: true, order: 452 },
  },
  {
    configKey: 'CatalogManagementPlugin',
    ossImport: () => import('./catalog-management/catalog-management'),
    ossClassName: 'CatalogManagementPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/catalog-management/catalog-management') : undefined,
    proClassName: 'CatalogManagementPro',
    defaultConfig: { enabled: true, order: 420 },
    isLoginRequired: true,
  },
  {
    configKey: 'StarManagementPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/stars/star-management') : undefined,
    proClassName: 'StarManagementPlugin',
    defaultConfig: { enabled: true, order: 420.5 },
  },
  {
    configKey: 'CatalogBrowserPlugin',
    ossImport: () => import('./catalog-browser/catalog-browser'),
    ossClassName: 'CatalogBrowserPlugin',
    defaultConfig: { enabled: true, order: 10 },
  },
  {
    configKey: 'HistoricCatalogPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/historic-catalog/historic-catalog') : undefined,
    proClassName: 'HistoricCatalogPlugin',
    defaultConfig: { enabled: true, order: 11 },
  },
  {
    configKey: 'CloseObjectsPlugin',
    ossImport: () => import('./close-objects/close-objects'),
    ossClassName: 'CloseObjectsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/close-objects/close-objects') : undefined,
    proClassName: 'CloseObjectsPro',
    defaultConfig: { enabled: true, order: 421 },
    isLoginRequired: true,
  },
  {
    configKey: 'BestPassPlugin',
    ossImport: () => import('./best-pass/best-pass'),
    ossClassName: 'BestPassPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/best-pass/best-pass') : undefined,
    proClassName: 'BestPassPro',
    defaultConfig: { enabled: true, order: 422 },
    isLoginRequired: true,
  },
  {
    configKey: 'OverflightPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/overflight/overflight') : undefined,
    proClassName: 'OverflightPlugin',
    defaultConfig: { enabled: true, order: 423 },
    isLoginRequired: true,
  },
  {
    configKey: 'TipAndCuePlanner',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/tip-and-cue/tip-and-cue') : undefined,
    proClassName: 'TipAndCuePlanner',
    defaultConfig: { enabled: true, order: 424 },
    isLoginRequired: true,
  },
  {
    configKey: 'OpticalSimulation',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/optical-simulation/optical-simulation') : undefined,
    proClassName: 'OpticalSimulation',
    defaultConfig: { enabled: true, order: 425 },
    isLoginRequired: true,
  },
  {
    configKey: 'TransitFinderPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/transit-finder/transit-finder') : undefined,
    proClassName: 'TransitFinderPlugin',
    defaultConfig: { enabled: true, order: 426 },
    isLoginRequired: true,
  },
  {
    configKey: 'EclipseSolarAnalysis',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/eclipse-solar-analysis/eclipse-solar-analysis') : undefined,
    proClassName: 'EclipseSolarAnalysis',
    defaultConfig: { enabled: true, order: 93 },
    isLoginRequired: true,
  },
  {
    configKey: 'CoverageAnalysis',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/coverage-analysis/coverage-analysis') : undefined,
    proClassName: 'CoverageAnalysis',
    defaultConfig: { enabled: true, order: 94 },
    isLoginRequired: true,
  },
  {
    configKey: 'SatelliteInterceptor',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/satellite-interceptor/satellite-interceptor') : undefined,
    proClassName: 'SatelliteInterceptor',
    defaultConfig: { enabled: true, order: 95 },
    isLoginRequired: true,
  },
  {
    configKey: 'ManeuverPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/maneuver/maneuver') : undefined,
    proClassName: 'ManeuverPlugin',
    defaultConfig: { enabled: true, order: 409 },
  },
  // Off because not ready: plugin doc marks it LEGACY, needing modernization; uses the legacy property pattern and its math lives in @ts-nocheck om-manager.ts pending a TypeScript rewrite.
  {
    configKey: 'InitialOrbitDeterminationPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/initial-orbit/initial-orbit') : undefined,
    proClassName: 'InitialOrbitDeterminationPlugin',
    defaultConfig: { enabled: false, order: 410 },
    isLoginRequired: true,
  },
  {
    configKey: 'Calculator',
    ossImport: () => import('./calculator/calculator'),
    ossClassName: 'Calculator',
    defaultConfig: { enabled: true, order: 400 },
  },

  // ── Plot Analysis ──────────────────────────────────────────────────────────
  {
    configKey: 'EciPlot',
    ossImport: () => import('./plot-analysis/eci-plots'),
    ossClassName: 'EciPlot',
    defaultConfig: { enabled: true, order: 260 },
  },
  {
    configKey: 'EcfPlot',
    ossImport: () => import('./plot-analysis/ecf-plots'),
    ossClassName: 'EcfPlot',
    defaultConfig: { enabled: true, order: 261 },
  },
  {
    configKey: 'RicPlot',
    ossImport: () => import('./plot-analysis/ric-plots'),
    ossClassName: 'RicPlot',
    defaultConfig: { enabled: true, order: 262 },
  },
  {
    configKey: 'Time2LonPlots',
    ossImport: () => import('./plot-analysis/time2lon'),
    ossClassName: 'Time2LonPlots',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/plot-analysis/time2lon') : undefined,
    proClassName: 'Time2LonPlotsPro',
    defaultConfig: { enabled: true, order: 263 },
    isLoginRequired: true,
  },
  {
    configKey: 'Lat2LonPlots',
    ossImport: () => import('./plot-analysis/lat2lon'),
    ossClassName: 'Lat2LonPlots',
    defaultConfig: { enabled: true, order: 264 },
  },
  {
    configKey: 'Inc2AltPlots',
    ossImport: () => import('./plot-analysis/inc2alt'),
    ossClassName: 'Inc2AltPlots',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/plot-analysis/inc2alt') : undefined,
    proClassName: 'Inc2AltPlotsPro',
    defaultConfig: { enabled: true, order: 265 },
    isLoginRequired: true,
  },
  {
    configKey: 'Inc2LonPlots',
    ossImport: () => import('./plot-analysis/inc2lon'),
    ossClassName: 'Inc2LonPlots',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/plot-analysis/inc2lon') : undefined,
    proClassName: 'Inc2LonPlotsPro',
    defaultConfig: { enabled: true, order: 266 },
    isLoginRequired: true,
  },
  {
    configKey: 'AltIncHeatmap',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/alt-inc-heatmap/alt-inc-heatmap') : undefined,
    proClassName: 'AltIncHeatmap',
    defaultConfig: { enabled: true, order: 267 },
    isLoginRequired: true,
  },
  {
    configKey: 'AzRangeHeatmap',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/az-range-heatmap/az-range-heatmap') : undefined,
    proClassName: 'AzRangeHeatmap',
    defaultConfig: { enabled: true, order: 268 },
    isLoginRequired: true,
  },

  // ── Settings & Utility ─────────────────────────────────────────────────────
  {
    configKey: 'FilterMenuPlugin',
    ossImport: () => import('./filter-menu/filter-menu'),
    ossClassName: 'FilterMenuPlugin',
    defaultConfig: { enabled: true, order: 592 },
  },
  {
    configKey: 'SymbologyPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/symbology/symbology-plugin') : undefined,
    proClassName: 'SymbologyPlugin',
    defaultConfig: { enabled: true, order: 593 },
    isLoginRequired: true,
  },
  {
    configKey: 'ColorSchemeEditorPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/color-scheme-editor/color-scheme-editor') : undefined,
    proClassName: 'ColorSchemeEditorPlugin',
    defaultConfig: { enabled: true, order: 594 },
    isLoginRequired: true,
  },
  {
    configKey: 'SettingsMenuPlugin',
    ossImport: () => import('./settings-menu/settings-menu'),
    ossClassName: 'SettingsMenuPlugin',
    defaultConfig: { enabled: true, order: 590 },
  },
  {
    configKey: 'SearchSettingsPlugin',
    ossImport: () => import('./search-settings/search-settings'),
    ossClassName: 'SearchSettingsPlugin',
    defaultConfig: { enabled: true, order: 589 },
  },
  {
    configKey: 'GraphicsSettingsPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/graphics-menu/graphics-settings') : undefined,
    proClassName: 'GraphicsSettingsPlugin',
    defaultConfig: { enabled: true, order: 590 },
  },
  {
    configKey: 'GraphicsMenuPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/graphics-menu/graphics-menu') : undefined,
    proClassName: 'GraphicsMenuPlugin',
    defaultConfig: { enabled: true, order: 591 },
  },
  {
    configKey: 'GamepadPlugin',
    ossImport: () => import('./gamepad/gamepad'),
    ossClassName: 'GamepadPlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'VideoDirectorPlugin',
    ossImport: () => import('./video-director/video-director'),
    ossClassName: 'VideoDirectorPlugin',
    defaultConfig: { enabled: true, order: 510 },
  },
  // Off by design: class is @deprecated; the main website now covers all of this functionality (disabled since 241631cb).
  {
    configKey: 'AboutMenuPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/about-menu/about-menu') : undefined,
    proClassName: 'AboutMenuPlugin',
    defaultConfig: { enabled: false, order: 601 },
  },
  {
    configKey: 'EarthPresetsPlugin',
    ossImport: () => import('./earth-presets/earth-presets'),
    ossClassName: 'EarthPresetsPlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'DrawLinesPlugin',
    ossImport: () => import('./draw-lines/draw-lines'),
    ossClassName: 'DrawLinesPlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'ViewInfoRmbPlugin',
    ossImport: () => import('./view-info-rmb/view-info-rmb'),
    ossClassName: 'ViewInfoRmbPlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'VcrPlugin',
    ossImport: () => import('./vcr/vcr'),
    ossClassName: 'VcrPlugin',
    defaultConfig: { enabled: true, order: 1000 },
  },
  {
    configKey: 'CommandPalettePlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/command-palette/command-palette') : undefined,
    proClassName: 'CommandPalettePlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'LaunchpadPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/launchpad/launchpad') : undefined,
    proClassName: 'LaunchpadPlugin',
    defaultConfig: { enabled: true },
  },
  {
    configKey: 'FavoritesMenuPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/favorites-menu/favorites-menu') : undefined,
    proClassName: 'FavoritesMenuPlugin',
    defaultConfig: { enabled: true },
  },
  // Off: registered disabled from day one (df4cc8c5) but the reason was not recorded; needs a maintainer decision.
  {
    configKey: 'CompanionLinkPlugin',
    proImport: __IS_PRO__ ? () => import(/* @vite-ignore */ '@plugins-pro/companion-link/companion-link') : undefined,
    proClassName: 'CompanionLinkPlugin',
    defaultConfig: { enabled: false, order: 520 },
    isLoginRequired: true,
  },

  // Off: shipped enabled in b3031739, then deliberately disabled in 46a74dd4 without a recorded reason; needs a maintainer decision.
  {
    configKey: 'PluginManagerPlugin',
    ossImport: () => import('./plugin-manager/plugin-manager'),
    ossClassName: 'PluginManagerPlugin',
    defaultConfig: { enabled: false, order: 950 },
  },

  // ── Onboarding (last: its tour targets look up other plugins) ─────────────
  {
    configKey: 'OnboardingPlugin',
    ossImport: () => import('./onboarding/onboarding'),
    ossClassName: 'OnboardingPlugin',
    // order -1 places Get Started above the built-in About entries (order 0,
    // e.g. View on GitHub) in the drawer's About group.
    defaultConfig: { enabled: true, order: -1 },
  },

  // ── External plugins (generated — always init after every built-in) ──────────
  // Empty upstream; populated by `npm run plugin -- sync` from installed clones.
  ...externalPluginManifest,
];
